import React, { useEffect, useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  LayoutDashboard, Calendar, CalendarRange, Users, DollarSign, BarChart2,
  Image, FileText, MessageSquare, LogOut, PlusCircle,
  Menu, X, User, ChevronRight, Home, UserCog, Shield, KeyRound, Settings, HardDriveDownload, CreditCard, MapPin,
} from 'lucide-react';
import { useViewport } from '../../hooks/useViewport';
import Logo from '../common/Logo';

// ─── Menu items config ────────────────────────────────────────────────────────
// module / action: if set, item is hidden for staff who lack that permission.
// ownerOnly: if true, item is hidden entirely for staff users.

const ALL_OWNER_MENU = [
  { path: '/owner/dashboard',       icon: LayoutDashboard, label: 'Dashboard',        module: 'dashboard',    action: 'view' },
  { path: '/owner/bookings',        icon: Calendar,        label: 'All Bookings',      module: 'bookings',     action: 'view' },
  { path: '/owner/create-booking',  icon: PlusCircle,      label: 'New Booking',       module: 'bookings',     action: 'view' },
  { path: '/owner/calendar',        icon: CalendarRange,   label: 'Calendar View',     module: 'rooms',        action: 'view' },
  { path: '/owner/rooms',           icon: Home,            label: 'Manage Rooms',      module: 'rooms',        action: 'view' },
  { path: '/owner/gallery',         icon: Image,           label: 'Gallery',           module: 'gallery',      action: 'view' },
  { path: '/owner/content',         icon: FileText,        label: 'Site Content',      module: 'site_content', action: 'view' },
  { path: '/owner/how-to-reach',   icon: MapPin,          label: 'How to Reach',      module: 'how_to_reach', action: 'view' },
  { path: '/owner/contacts',        icon: MessageSquare,   label: 'Enquiries',         module: 'contacts',     action: 'view' },
  { path: '/owner/reports',         icon: BarChart2,       label: 'Reports',           module: 'reports',      action: 'view' },
  { path: '/owner/payments',        icon: CreditCard,      label: 'Payments',          ownerOnly: true },
  { path: '/owner/agents',          icon: UserCog,         label: 'Manage Agents',     ownerOnly: true },
  { path: '/owner/users',           icon: Users,           label: 'Manage Users',      ownerOnly: true },
  { path: '/owner/portal-users',    icon: Shield,          label: 'Portal Users',      module: 'portal_users', action: 'view' },
  { path: '/owner/permission-roles',icon: KeyRound,        label: 'Permission Roles',  ownerOnly: true },
  { path: '/owner/settings',        icon: Settings,             label: 'Settings',   ownerOnly: true },
  { path: '/owner/backup',          icon: HardDriveDownload,    label: 'Backup',     ownerOnly: true },
];

const getMenuItems = (userType, hasPermission) => {
  switch (userType) {
    case 'customer':
      return [
        { path: '/customer/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
        { path: '/customer/book-room', icon: Calendar,        label: 'Book a Room' },
        { path: '/customer/bookings',  icon: FileText,        label: 'My Bookings' },
        { path: '/customer/profile',   icon: User,            label: 'Profile' },
      ];
    case 'agent':
      return [
        { path: '/agent/dashboard',       icon: LayoutDashboard, label: 'Dashboard' },
        { path: '/agent/create-booking',  icon: Calendar,        label: 'Create Booking' },
        { path: '/agent/customers',       icon: Users,           label: 'Customers' },
        { path: '/agent/commissions',     icon: DollarSign,      label: 'Commissions' },
      ];
    case 'owner':
      // Owner sees all items (no filtering)
      return ALL_OWNER_MENU.filter((item) => !item.ownerOnly || true);
    default:
      // staff — filter by permissions
      return ALL_OWNER_MENU.filter((item) => {
        if (item.ownerOnly) return false;
        if (item.module && item.action) {
          return hasPermission(item.module, item.action);
        }
        return true;
      });
  }
};

const roleColors = {
  customer: '#16a34a',
  agent:    '#2563eb',
  owner:    '#7c3aed',
  staff:    '#ea580c',
};

const roleLabels = {
  customer: 'Customer',
  agent:    'Agent',
  owner:    'Owner',
  staff:    'Staff',
};

const DashboardLayout = ({ userType }) => {
  const { logout, user, hasPermission } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { isDesktop } = useViewport();

  // Determine effective userType (staff uses owner routes but filtered menu)
  const effectiveType = user?.user_type === 'staff' ? 'staff' : userType;
  const menuItems = getMenuItems(effectiveType, hasPermission);

  const isActive = (path) => location.pathname === path;

  useEffect(() => {
    if (isDesktop) setSidebarOpen(false);
  }, [isDesktop]);

  const sidebarStyle = {
    width: 248,
    backgroundColor: '#1f2937',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  };

  const navItemStyle = (active) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 14px',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 500,
    textDecoration: 'none',
    marginBottom: 2,
    transition: 'all 0.2s',
    backgroundColor: active ? '#16a34a' : 'transparent',
    color: active ? '#ffffff' : 'rgba(255,255,255,0.65)',
  });

  // NOTE: this is rendered via a function call ({renderSidebar()}), NOT as
  // <renderSidebar />. Rendering it as a JSX element would make React treat it
  // as a fresh component type on every re-render (e.g. on route change) and
  // remount the <nav>, resetting its scroll position to the top.
  const renderSidebar = () => (
    <div style={sidebarStyle}>
      {/* Logo */}
      <div style={{ padding: '22px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
          <Logo height={40} onDark style={{ width: '100%', padding: '10px 12px' }} imgStyle={{ margin: '0 auto' }} />
        </Link>
      </div>

      {/* User Info */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 38, height: 38, borderRadius: 10, backgroundColor: 'rgba(22,163,74,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, color: '#4ade80', flexShrink: 0 }}>
            {user?.name?.charAt(0).toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 13, fontWeight: 600, color: '#ffffff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', margin: 0 }}>{user?.name}</p>
            <span style={{ display: 'inline-block', fontSize: 10, fontWeight: 500, color: '#ffffff', backgroundColor: roleColors[user?.user_type] || '#6b7280', padding: '1px 8px', borderRadius: 20, marginTop: 2 }}>
              {roleLabels[user?.user_type] || user?.user_type}
            </span>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav style={{ flex: 1, overflowY: 'auto', padding: '12px 12px' }}>
        {menuItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            onClick={() => setSidebarOpen(false)}
            style={navItemStyle(isActive(item.path))}
          >
            <item.icon size={17} />
            <span style={{ flex: 1 }}>{item.label}</span>
            {isActive(item.path) && <ChevronRight size={13} />}
          </Link>
        ))}
      </nav>

      {/* Bottom Actions */}
      <div style={{ padding: '12px 12px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <Link
          to="/"
          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, fontSize: 13, color: 'rgba(255,255,255,0.5)', textDecoration: 'none', marginBottom: 2 }}
        >
          <Home size={17} />
          <span>Back to Website</span>
        </Link>
        <button
          onClick={logout}
          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, fontSize: 13, color: '#f87171', border: 'none', backgroundColor: 'transparent', width: '100%', cursor: 'pointer' }}
        >
          <LogOut size={17} />
          <span>Logout</span>
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f3f4f6' }}>
      {/* Desktop Sidebar */}
      {isDesktop && (
        <aside style={{ position: 'fixed', top: 0, left: 0, height: '100%', zIndex: 30, boxShadow: '4px 0 20px rgba(0,0,0,0.15)' }}>
          {renderSidebar()}
        </aside>
      )}

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 40, animation: 'fadeIn 0.2s ease' }}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      {!isDesktop && (
        <aside style={{
          position: 'fixed', top: 0, left: 0, height: '100%', zIndex: 50,
          transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.3s ease',
          boxShadow: '4px 0 20px rgba(0,0,0,0.2)',
        }}>
          <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 1 }}>
            <button onClick={() => setSidebarOpen(false)} style={{ border: 'none', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: 6, padding: 4, cursor: 'pointer', color: '#ffffff', display: 'flex' }}>
              <X size={16} />
            </button>
          </div>
          {renderSidebar()}
        </aside>
      )}

      {/* Main Content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh', marginLeft: isDesktop ? 248 : 0 }}>
        {/* Top Bar (mobile) */}
        {!isDesktop && (
          <div style={{ position: 'sticky', top: 0, zIndex: 20, backgroundColor: '#ffffff', borderBottom: '1px solid #e5e7eb', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <button
              onClick={() => setSidebarOpen(true)}
              style={{ border: 'none', backgroundColor: 'transparent', cursor: 'pointer', padding: 6, borderRadius: 8, display: 'flex', alignItems: 'center' }}
            >
              <Menu size={22} color="#374151" />
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Logo height={34} />
            </div>
            <div style={{ width: 36 }} />
          </div>
        )}

        {/* Page Content */}
        <main style={{ flex: 1, padding: '24px 20px' }}>
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
